import { Controller } from "@hotwired/stimulus"

// Keeps the sidebar's scroll position steady across Turbo navigations.
//
// The sidebar is re-rendered on every navigation (it is not Turbo-permanent), so
// this scroll container is rebuilt with scrollTop 0 each time. We capture the exact
// outgoing scrollTop on `turbo:before-render` (old DOM still present) and reapply it
// when the new container connects — an exact per-navigation hand-off held in memory.
//
// This replaces the previous sessionStorage + live `scroll` listener design, which
// re-saved on every scroll (including render-triggered ones) so the stored number
// drifted downward and got re-applied larger each click — the "nav ratchets up as I
// click through items" bug. Clicking a top-level or Tools/section link must leave the
// nav exactly where it was; the ONLY intentional movement is revealing an active item
// that lives inside an Admin submenu group (.gl-sidebar-group).
//
// Runs only when expanded (> 1200px): collapsed, the container height is ~0 and the
// browser clamps scrollTop, so capture/restore would just store a meaningless 0.
const DESKTOP_BREAKPOINT = 1200

// Module-scoped so the captured position survives the disconnect→connect gap while
// Turbo swaps the sidebar element.
const savedScrollTop = new Map()

// Test-only: clear cross-navigation state between examples (module state otherwise
// persists across the whole test file).
export const __resetSidebarScrollStateForTest = () => savedScrollTop.clear()

export default class extends Controller {
  connect() {
    // Register unconditionally and gate INSIDE #capture/#restore on the current
    // width. Evaluating #isDesktop() once here would permanently skip capture for a
    // page that loads narrow (<=1200px) and is then resized wider without a Turbo
    // navigation — the outgoing scroll of that one transition would be lost.
    this.boundCapture = this.#capture.bind(this)
    document.addEventListener("turbo:before-render", this.boundCapture)

    this.#restore()
    // Re-apply after layout settles (badges/cards can change height post-connect).
    requestAnimationFrame(() => this.#restore())
  }

  disconnect() {
    document.removeEventListener("turbo:before-render", this.boundCapture)
  }

  // Runs while the OLD sidebar is still in the DOM (turbo:before-render), so
  // this.element.scrollTop is the position the user is leaving.
  #capture() {
    if (!this.#isDesktop()) return
    savedScrollTop.set(this.#mode(), this.element.scrollTop)
  }

  #restore() {
    if (!this.#isDesktop()) return
    const saved = savedScrollTop.get(this.#mode())
    if (typeof saved === "number") this.element.scrollTop = saved
    this.#revealActiveSubmenu()
  }

  // Reveal ONLY an active item inside an Admin submenu group. Top-level links and
  // Tools/Agency/Admin section links have no group ancestor, so the nav stays exactly
  // where #restore left it — clicking them never moves it.
  #revealActiveSubmenu() {
    const active = this.element.querySelector([
      ".gl-sidebar-group-toggle.active",
      ".gl-sidebar-link.active",
      ".gl-sidebar-sublink.active"
    ].join(", "))
    if (!active) return

    const group = active.closest(".gl-sidebar-group")
    if (!group) return

    const containerRect = this.element.getBoundingClientRect()
    const groupRect = group.getBoundingClientRect()
    const offsetTop = groupRect.top - containerRect.top
    const offsetBottom = groupRect.bottom - containerRect.top

    // Already fully visible → don't yank it.
    if (offsetTop >= 0 && offsetBottom <= this.element.clientHeight) return

    this.element.scrollTop = Math.max(this.element.scrollTop + offsetTop - 96, 0)
  }

  #mode() {
    return this.#sidebar()?.classList.contains("gl-sidebar--mode-settings") ? "settings" : "main"
  }

  #sidebar() {
    return this.element.closest(".gl-sidebar")
  }

  #isDesktop() {
    return window.innerWidth > DESKTOP_BREAKPOINT
  }
}
